import { Container, Filter, GlProgram, Sprite, Texture } from "pixi.js";

const SIDE_ALPHA = 0.55;
const TOP_ALPHA = 0.1;
const BOTTOM_ALPHA = 0.25;
const SIDE_FALLOFF = 0.25;
const TOP_FALLOFF = 0.2;
const BOTTOM_FALLOFF = 0.2;
const EASE_EXPONENT = 2.2;
const DITHER_STRENGTH = 1.0 / 16.0;

const VERTEX = `
attribute vec2 aPosition;
varying vec2 vTextureCoord;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;

void main(void) {
  gl_Position = vec4(aPosition * 2.0 - 1.0, 0.0, 1.0);
  vTextureCoord = aPosition;
}
`;

const FRAGMENT = `
precision highp float;

varying vec2 vTextureCoord;

uniform float uSideAlpha;
uniform float uTopAlpha;
uniform float uBottomAlpha;
uniform float uSideFalloff;
uniform float uTopFalloff;
uniform float uBottomFalloff;
uniform float uEaseExponent;
uniform float uDitherStrength;

float bayer4(int x, int y) {
  int idx = x + y * 4;
  if (idx ==  0) return  0.0 / 16.0;
  if (idx ==  1) return  8.0 / 16.0;
  if (idx ==  2) return  2.0 / 16.0;
  if (idx ==  3) return 10.0 / 16.0;
  if (idx ==  4) return 12.0 / 16.0;
  if (idx ==  5) return  4.0 / 16.0;
  if (idx ==  6) return 14.0 / 16.0;
  if (idx ==  7) return  6.0 / 16.0;
  if (idx ==  8) return  3.0 / 16.0;
  if (idx ==  9) return 11.0 / 16.0;
  if (idx == 10) return  1.0 / 16.0;
  if (idx == 11) return  9.0 / 16.0;
  if (idx == 12) return 15.0 / 16.0;
  if (idx == 13) return  7.0 / 16.0;
  if (idx == 14) return 13.0 / 16.0;
  return 5.0 / 16.0;
}

float edgeFalloff(float t, float zone, float maxAlpha) {
  float ratio = clamp(1.0 - t / zone, 0.0, 1.0);
  return maxAlpha * pow(ratio, uEaseExponent);
}

void main(void) {
  vec2 uv = vTextureCoord;

  float left   = edgeFalloff(uv.x,         uSideFalloff,   uSideAlpha);
  float right  = edgeFalloff(1.0 - uv.x,   uSideFalloff,   uSideAlpha);
  float top    = edgeFalloff(uv.y,         uTopFalloff,    uTopAlpha);
  float bottom = edgeFalloff(1.0 - uv.y,   uBottomFalloff, uBottomAlpha);

  float a = 0.0;
  a = left   + a * (1.0 - left);
  a = right  + a * (1.0 - right);
  a = top    + a * (1.0 - top);
  a = bottom + a * (1.0 - bottom);

  int bx = int(mod(gl_FragCoord.x, 4.0));
  int by = int(mod(gl_FragCoord.y, 4.0));
  float dither = (bayer4(bx, by) - 0.5) * uDitherStrength;

  a = clamp(a + dither, 0.0, 1.0);

  gl_FragColor = vec4(0.0, 0.0, 0.0, a);
}
`;

export class Vignette {
  private sprite: Sprite;
  private filter: Filter;

  constructor(stage: Container) {
    this.sprite = new Sprite(Texture.WHITE);

    this.filter = new Filter({
      glProgram: new GlProgram({ vertex: VERTEX, fragment: FRAGMENT }),
      resources: {
        vignetteUniforms: {
          uSideAlpha:     { value: SIDE_ALPHA,     type: "f32" },
          uTopAlpha:      { value: TOP_ALPHA,      type: "f32" },
          uBottomAlpha:   { value: BOTTOM_ALPHA,   type: "f32" },
          uSideFalloff:   { value: SIDE_FALLOFF,   type: "f32" },
          uTopFalloff:    { value: TOP_FALLOFF,    type: "f32" },
          uBottomFalloff: { value: BOTTOM_FALLOFF, type: "f32" },
          uEaseExponent:  { value: EASE_EXPONENT,  type: "f32" },
          uDitherStrength:{ value: DITHER_STRENGTH, type: "f32" },
        },
      },
    });

    this.sprite.filters = [this.filter];

    stage.addChild(this.sprite);
  }

  draw(props: { width: number; height: number }): void {
    this.sprite.width = props.width;
    this.sprite.height = props.height;
  }

  destroy(): void {
    this.filter.destroy();
    this.sprite.destroy();
  }
}
